import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { INavTab } from '@features/social-analysis/models/INavTab';
import { TranslatePipe } from '@ngx-translate/core';
import { NavMenuComponent } from '@shared/components/nav-menu/nav-menu.component';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { APP_SLUGS, DATA_SLUGS, MAIN_SLUGS } from '@routes/routes.types';
import { ListIconComponent } from '@shared/components/icons/list-icon/list-icon.component';
import { SIDEBAR_ICONS } from '@core/layout/sidebar/sidebar.config';
import { TicketActionService } from './services/ticket-action.service';

@Component({
    selector: 'app-ticket-layout',
    standalone: true,
    imports: [RouterOutlet, PageTitleComponent, NavMenuComponent, TranslatePipe],
    template: `
        <div class="flex h-full w-full flex-1 flex-col bg-twMainBackground">
            <header class="shrink-0 px-8 pt-4">
                <div class="mx-auto w-full max-w-[1680px]">
                    <tw-page-title
                        class="block pb-6"
                        [lucideIcon]="icon"
                        [title]="TICKET_TRANSLATION_KEYS.title" />

                    <div class="flex items-end justify-between gap-4">
                        <app-nav-menu [routeExact]="false" [tabs]="tabs" />

                        <div class="flex items-center gap-2">
                            @for (btn of actionButtons(); track btn.label) {
                                @if (btn.variant === 'outline') {
                                    <button class="tw-btn-secondary" (click)="btn.action()">
                                        {{ btn.label | translate }}
                                    </button>
                                } @else {
                                    <button class="tw-btn-primary" (click)="btn.action()">
                                        {{ btn.label | translate }}
                                    </button>
                                }
                            }
                            <ng-content select="[buttons]" />
                        </div>
                    </div>
                </div>
            </header>

            <div class="mx-auto flex w-full min-h-0 max-w-[1680px] flex-1 flex-col">
                <router-outlet />
                <ng-content select="[body]" />
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [':host { display: flex; width: 100%; height: 100%; }'],
})
export class TicketLayoutComponent {
    private readonly actionService = inject(TicketActionService);
    protected readonly actionButtons = this.actionService.actionButtons;
    protected readonly icon = SIDEBAR_ICONS.ticket;
    protected readonly TICKET_TRANSLATION_KEYS = TRANSLATION_KEYS.ticket;

    protected readonly tabs: INavTab[] = [
        {
            id: 'ticket',
            icon: ListIconComponent,
            label: TRANSLATION_KEYS.ticket.tabs.tickets,
            route: `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.TICKET}/${DATA_SLUGS.ticket}`,
        },
        {
            id: 'fields',
            icon: ListIconComponent,
            label: TRANSLATION_KEYS.ticket.tabs.fields,
            route: `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.TICKET}/${DATA_SLUGS.adminFields}`,
        },
        {
            id: 'stage',
            icon: ListIconComponent,
            label: TRANSLATION_KEYS.ticket.tabs.stages,
            route: `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.TICKET}/${DATA_SLUGS.stage}`,
        },
        {
            id: 'config',
            icon: ListIconComponent,
            label: TRANSLATION_KEYS.ticket.tabs.configuration,
            route: `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.TICKET}/${DATA_SLUGS.ticketConfig}`,
        },
    ];
}
